import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ArrowLeft, ShieldCheck, User as UserIcon } from "lucide-react";

const EMPTY_FORM = { email: "", password: "", name: "", language: "zh", role: "user" };

export default function AdminUsers() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'edit'
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setDialogMode("create");
    setForm(EMPTY_FORM);
    setEditingId(null);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setDialogMode("edit");
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      language: u.language || "zh",
      role: u.role || "user",
    });
    setEditingId(u.id);
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (dialogMode === "create") {
        await api.post("/admin/users", form);
        toast.success(t("admin_user_created"));
      } else {
        const body = { ...form };
        if (!body.password) delete body.password;
        await api.patch(`/admin/users/${editingId}`, body);
        toast.success(t("admin_user_updated"));
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(t("admin_user_deleted"));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <AppHeader />
      <div className="flex-1 overflow-auto p-6 md:p-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground tx-bg mb-2"
              >
                <ArrowLeft className="w-3 h-3" strokeWidth={1.75} />
                {t("back_to_workspace")}
              </Link>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                {t("admin_users_title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("admin_users_subtitle")}
              </p>
            </div>
            <button
              data-testid="admin-create-user-btn"
              onClick={openCreate}
              className="btn-swiss primary h-10 gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              {t("admin_add_user")}
            </button>
          </div>

          <div className="border border-border rounded-sm bg-background">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left">
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-widest">
                    {t("display_name")}
                  </th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-widest">
                    {t("email")}
                  </th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-widest">
                    {t("language")}
                  </th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-widest">
                    {t("role")}
                  </th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-right">
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody data-testid="admin-users-table">
                {loading && (
                  <tr>
                    <td colSpan={5} className="p-6 text-sm text-muted-foreground text-center">
                      …
                    </td>
                  </tr>
                )}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-6 text-sm text-muted-foreground text-center">
                      {t("admin_no_users")}
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    className="border-b border-border/60 last:border-0 hover:bg-accent/40 tx-bg"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md border-2 border-[hsl(var(--brand))] bg-white flex items-center justify-center font-display font-bold text-xs uppercase text-[hsl(var(--brand))]">
                          {(u.name || u.email || "?")[0]}
                        </div>
                        <span className="font-semibold">{u.name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className="pill text-[10px]">
                        {u.language === "en" ? "English" : "中文"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-primary font-semibold">
                          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                          {t("role_admin")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                          <UserIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
                          {t("role_user")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          data-testid={`admin-edit-${u.id}`}
                          className="btn-swiss h-8 px-2 gap-1 text-xs"
                          onClick={() => openEdit(u)}
                        >
                          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </button>
                        <button
                          data-testid={`admin-delete-${u.id}`}
                          className="btn-swiss danger h-8 px-2 gap-1 text-xs"
                          disabled={u.id === user?.id}
                          onClick={() => setDeleteTarget(u)}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {dialogMode === "create" ? t("admin_add_user") : t("admin_edit_user")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <UserField label={t("display_name")} testid="admin-form-name">
              <input
                className="w-full h-10 px-3 border border-border rounded-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary tx-bg"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </UserField>
            <UserField label={t("email")} testid="admin-form-email">
              <input
                type="email"
                className="w-full h-10 px-3 border border-border rounded-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary tx-bg font-mono"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </UserField>
            <UserField
              label={
                dialogMode === "create" ? t("password") : `${t("password")} (${t("optional")})`
              }
              testid="admin-form-password"
            >
              <input
                type="password"
                minLength={6}
                autoComplete="new-password"
                placeholder={dialogMode === "edit" ? "•••••• (leave blank to keep)" : ""}
                className="w-full h-10 px-3 border border-border rounded-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary tx-bg"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </UserField>
            <UserField label={t("language")} testid="admin-form-language">
              <div className="inline-flex border border-border rounded-sm overflow-hidden">
                {[
                  { v: "zh", l: "中文" },
                  { v: "en", l: "English" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForm({ ...form, language: o.v })}
                    className={`px-4 h-10 text-sm font-mono tx-bg border-r border-border last:border-r-0 ${
                      form.language === o.v
                        ? "bg-primary text-primary-foreground"
                        : "bg-background hover:bg-accent"
                    }`}
                    data-testid={`admin-form-lang-${o.v}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </UserField>
            <UserField label={t("role")} testid="admin-form-role">
              <div className="inline-flex border border-border rounded-sm overflow-hidden">
                {[
                  { v: "user", l: t("role_user"), Icon: UserIcon },
                  { v: "admin", l: t("role_admin"), Icon: ShieldCheck },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForm({ ...form, role: o.v })}
                    className={`px-3 h-10 text-xs font-mono uppercase tracking-widest tx-bg border-r border-border last:border-r-0 inline-flex items-center gap-1.5 ${
                      form.role === o.v
                        ? "bg-primary text-primary-foreground"
                        : "bg-background hover:bg-accent"
                    }`}
                    data-testid={`admin-form-role-${o.v}`}
                  >
                    <o.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {o.l}
                  </button>
                ))}
              </div>
            </UserField>
          </div>
          <DialogFooter>
            <button className="btn-swiss" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </button>
            <button
              data-testid="admin-form-submit"
              className="btn-swiss primary"
              onClick={submit}
              disabled={saving}
            >
              {saving ? "…" : dialogMode === "create" ? t("admin_add_user") : t("save")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              {t("admin_delete_user")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin_confirm_delete", { name: deleteTarget?.name || deleteTarget?.email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserField({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="block text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
